import { useState, useCallback } from "react";
import { submitForm } from "@/lib/api";

/**
 * Generic form hook.
 *
 * @param formType - one of the backend discriminator strings
 * @param initialValues - default field values
 * @param validate - function(values) → errors object (field → message)
 * @returns { values, errors, serverError, submitting, success, submissionId,
 *           handleChange, handleBlur, handleSubmit, reset }
 */
export const useFormSubmit = ({ formType, initialValues, validate }) => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [serverError, setServerError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submissionId, setSubmissionId] = useState(null);

    const handleChange = useCallback((name, value) => {
        setValues((v) => ({ ...v, [name]: value }));
        // Clear error on edit
        setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
        setServerError(null);
    }, []);

    const handleBlur = useCallback(
        (name) => {
            setTouched((t) => ({ ...t, [name]: true }));
            if (!validate) return;
            const next = validate({ ...values });
            if (next[name]) {
                setErrors((e) => ({ ...e, [name]: next[name] }));
            }
        },
        [values, validate],
    );

    const focusFirstError = (errMap) => {
        const first = Object.keys(errMap).find((k) => errMap[k]);
        if (first) {
            const el =
                document.getElementById(first) ||
                document.querySelector(`[name="${first}"]`);
            el?.focus?.();
        }
    };

    const handleSubmit = useCallback(
        async (e) => {
            if (e && e.preventDefault) e.preventDefault();
            setServerError(null);

            const localErrors = validate ? validate(values) : {};
            const filtered = Object.fromEntries(
                Object.entries(localErrors).filter(([, v]) => v),
            );
            if (Object.keys(filtered).length) {
                setErrors(filtered);
                // Mark every error field as touched
                const t = {};
                Object.keys(filtered).forEach((k) => (t[k] = true));
                setTouched((prev) => ({ ...prev, ...t }));
                focusFirstError(filtered);
                return;
            }

            setSubmitting(true);
            // Strip the honeypot key from `values` is fine — we send it explicitly
            const payload = { ...values };
            // Cast empty strings to undefined so backend nullable fields pass
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = undefined;
            });

            const result = await submitForm(formType, payload);
            setSubmitting(false);

            if (result.ok) {
                setSuccess(true);
                setSubmissionId(result.submissionId);
                // Scroll the form into view (top of form area)
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }
            if (result.status === 400 && result.errors) {
                const mapped = {};
                Object.entries(result.errors).forEach(([k, msg]) => {
                    if (k === "_form") setServerError(msg);
                    else mapped[k] = msg;
                });
                setErrors(mapped);
                focusFirstError(mapped);
                return;
            }
            setServerError(result.error || "We could not submit your form.");
        },
        [values, validate, formType],
    );

    const reset = useCallback(() => {
        setValues(initialValues);
        setErrors({});
        setTouched({});
        setServerError(null);
        setSuccess(false);
        setSubmissionId(null);
    }, [initialValues]);

    return {
        values,
        errors,
        touched,
        serverError,
        submitting,
        success,
        submissionId,
        handleChange,
        handleBlur,
        handleSubmit,
        reset,
    };
};

/* ---------- Common validators ---------- */
export const isNonEmpty = (v) =>
    !!v && typeof v === "string" && v.trim().length > 0;
export const isEmail = (v) =>
    typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
export const isPhone = (v) =>
    typeof v === "string" && /^[0-9 +\-().]{7,25}$/.test(v.trim());

export const minLength = (v, n) =>
    typeof v === "string" && v.trim().length >= n;
